import { Matrix3, Matrix4, Vector3 } from 'three';

// The geometry behind a gizmo-driven move, kept free of Three.js scene state so it can be tested
// and reasoned about on its own.

// Restricts a free drag delta to a single axis. An arrow gives the raycaster no surface of its own,
// so the drag runs against a camera-facing plane and only the component along the axis is kept —
// the same projection GeometryDirectionalMoveCmd applies to a directional edit handle.
export function constrainDeltaToAxis(worldDelta: Vector3, axis: Vector3): Vector3 {
    if (axis.lengthSq() < 1e-10) return worldDelta.clone();

    const unitAxis = axis.clone().normalize();
    return unitAxis.multiplyScalar(worldDelta.dot(unitAxis));
}

// Scene object data is stored in its parent product's coordinate system, so a world-space drag has
// to be re-expressed there before it can be applied. Only the rotation/scale part of the parent
// matrix takes part: a delta is a direction, not a position.
export function worldDeltaToLocal(worldDelta: Vector3, parentWorldMatrix: Matrix4): Vector3 {
    const inverseRotationScale = new Matrix3().setFromMatrix4(parentWorldMatrix.clone().invert());
    return worldDelta.clone().applyMatrix3(inverseRotationScale);
}

// Every SceneObjectData.applyMatrix4 implementation transforms absolute points but only rotates its
// direction and offset fields, so a pure translation moves the whole object and leaves orientation,
// leader offsets and profile points alone. That makes this a type-agnostic whole-object move, with
// no per-data-class translate method to keep in sync.
export function translationMatrix(localDelta: Vector3): Matrix4 {
    return new Matrix4().makeTranslation(localDelta.x, localDelta.y, localDelta.z);
}
